/**
 * Hook for fetching report data with optional grouping and aggregation
 *
 * Uses the generic report API to fetch data with:
 * - Dynamic grouping by any field
 * - Aggregations (sum, avg, count)
 * - Standard Frappe filters
 */

import { useQuery } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import type { ReportData, AggregationType } from "@/lib/erp/reports";
import type { FilterDescriptor } from "@/lib/erp";
import { mapFiltersToFrappe } from "@/lib/erp/filterMapper";
import { stableStringify } from "@/lib/utils/stableStringify";

export interface UseReportDataParams {
  doctype: string;
  fields?: string[];
  groupBy?: string;
  aggregations?: Record<string, AggregationType>;
  filterDescriptors?: FilterDescriptor[];
  orderBy?: string;
  enabled?: boolean;
}

export function useReportData(params: UseReportDataParams) {
  const {
    doctype,
    fields,
    groupBy,
    aggregations,
    filterDescriptors,
    orderBy,
    enabled = true,
  } = params;

  // Map FilterDescriptor[] to Frappe filter format
  const filters =
    filterDescriptors && filterDescriptors.length > 0
      ? mapFiltersToFrappe(doctype, filterDescriptors)
      : undefined;

  // When groupBy is set, fetch flat data with the group field included.
  // RevoGrid handles visual grouping client-side (expand/collapse rows).
  // Server-side GROUP BY only returns aggregated summary rows (no individual items).
  const effectiveFields = groupBy && fields
    ? (fields.includes(groupBy) ? fields : [...fields, groupBy])
    : groupBy
      ? [groupBy]
      : fields;

  const hasAggregationsOnly = groupBy
    && aggregations
    && Object.keys(aggregations).length > 0
    && !fields?.length;

  // Build stable query key including ALL params that affect data
  const queryKey = [
    "report-data",
    doctype,
    {
      fields: stableStringify(effectiveFields),
      groupBy: groupBy ?? null,
      aggregations: stableStringify(aggregations ?? {}),
      filters: stableStringify(filters ?? []),
      orderBy: orderBy ?? null,
      serverGrouped: hasAggregationsOnly,
    },
  ];

  return useQuery<ReportData>({
    queryKey,
    queryFn: () =>
      erp.reports.getData({
        doctype,
        fields: effectiveFields,
        // Only use server-side GROUP BY when user wants pure aggregation summary
        // (aggregations set but no visible columns). Otherwise fetch flat data
        // so RevoGrid can show individual items inside expandable groups.
        group_by: hasAggregationsOnly ? groupBy : undefined,
        aggregations: hasAggregationsOnly ? aggregations : undefined,
        filters,
        order_by: orderBy,
        // Flat mode with grouping needs all rows for proper visual grouping
        limit_page_length: groupBy && !hasAggregationsOnly ? 0 : undefined,
      }),
    enabled: enabled && !!doctype,
    staleTime: 30 * 1000, // 30 seconds
    placeholderData: (previousData) => previousData,
  });
}
